export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code = "",
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  let response: Response;
  try {
    const headers = new Headers(options.headers);
    if (!headers.has("Content-Type"))
      headers.set("Content-Type", "application/json");
    response = await fetch(`/api/v1${path}`, {
      ...options,
      credentials: "same-origin",
      cache: "no-store",
      headers,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw error;
    throw new ApiError(0, "Unable to reach the Panel service.");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    if (response.status === 401 && !path.startsWith("/auth/")) {
      window.dispatchEvent(new Event("velnqo:unauthorized"));
    }
    if (body.code === "session_changed")
      window.dispatchEvent(new Event("velnqo:session-changed"));
    throw new ApiError(
      response.status,
      body.error || `Request failed (${response.status}).`,
      body.code || "",
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function mutation<T>(
  path: string,
  body: unknown,
  method = "POST",
  options: RequestInit = {},
) {
  return api<T>(path, { ...options, method, body: JSON.stringify(body) });
}

export function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong.";
}
export function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes < 0) return "--";
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let index = 0;
  while (bytes >= 1024 && index < units.length - 1) {
    bytes /= 1024;
    index++;
  }
  return `${bytes.toLocaleString(undefined, { maximumFractionDigits: index ? 1 : 0 })} ${units[index]}`;
}
export function encodeFile(content: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < content.length; i += 8192)
    binary += String.fromCharCode(...content.subarray(i, i + 8192));
  return btoa(binary);
}
export function decodeFile(content: string): Uint8Array {
  return Uint8Array.from(atob(content), (c) => c.charCodeAt(0));
}
export function download(
  content: BlobPart,
  name: string,
  type = "application/octet-stream",
) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
